// ORDER sections for the SyQi dashboard — the five collapsible colored bands
// (Orient / Resources / Directions / Execute / Revise). Each band hosts the
// embedded widgets of the tools the user has toggled on in SyQi settings.
//
// Storage: user_settings.syqi_widgets jsonb gains an OPTIONAL `sections` key
// ({ sections: { [gear]: toolId[] } }) alongside the legacy `widgets` array.
// No new DB column needed; writers must spread the existing column value so
// the legacy key survives (useSyqiSections below does this).
import { Gear, GEARS, TOOL_NAMES } from '../domain/olmps';
import { useSettingColumn } from './settings/useSettingColumn';

export const ORDER_SECTIONS: Gear[] = GEARS;

export const ORDER_SECTION_META: Record<
  Gear,
  {
    title: string;
    question: string;
    /** Band color (header) — mid tone of the gear's metal. */
    color: string;
    /** Bright end of the brushed-metal gradient (top-left sheen). */
    sheenHi: string;
    /** Dark end of the brushed-metal gradient (bottom-right). */
    sheenLo: string;
    /** Lighter tint for the expanded body behind widgets. */
    tint: string;
    /** Deep accent for score text / chevrons, used on the light tint body. */
    deep: string;
    /** Ink colour that sits ON the metal band itself (contrast-checked). */
    onBand: string;
  }
> = {
  // Metal palette — each gear is a different metal. `color` is the mid tone of
  // the band, `sheenHi`/`sheenLo` are the ends of its brushed-metal gradient,
  // `tint` the expanded body behind widgets, `deep` the ink on the band.
  orient: { title: 'Orient', question: 'Where am I going?', color: '#E0BB4A', sheenHi: '#FFF3C0', sheenLo: '#A6801E', tint: '#FBF3D8', deep: '#6B5108', onBand: '#3D2D04' },
  regulate: { title: 'Resources', question: 'What resources do I need?', color: '#D68F5C', sheenHi: '#FCD9B8', sheenLo: '#9C5C2E', tint: '#F8E7D6', deep: '#6E3E15', onBand: '#3E2409' },
  direct: { title: 'Directions', question: 'How will I get there?', color: '#E3B9A8', sheenHi: '#FFE3D3', sheenLo: '#B37E68', tint: '#FBEBE3', deep: '#7A3F26', onBand: '#4A2413' }, // Rose Gold
  execute: { title: 'Execute', question: 'Getting there.', color: '#E4E6E8', sheenHi: '#FFFFFF', sheenLo: '#B7BABE', tint: '#F7F8F9', deep: '#5A5E63', onBand: '#2C2F33' },
  revise: { title: 'Revise', question: 'What changed?', color: '#BFC4C9', sheenHi: '#F5F7F9', sheenLo: '#8B9096', tint: '#F0F2F4', deep: '#464A4F', onBand: '#23262A' }, // Silver
};

// Tool roster (SECTION_TOOLS / SECTION_DEFAULTS / enabledToolsFor) lives in
// syqiRoster.ts — the single source of truth shared with the backend's
// /api/ai/overview route. Re-exported here for existing consumers.
export { SECTION_TOOLS, SECTION_DEFAULTS, enabledToolsFor } from './syqiRoster';
export type { SyqiSections } from './syqiRoster';
import { SECTION_TOOLS, SECTION_DEFAULTS, enabledToolsFor, SyqiSections } from './syqiRoster';

export function sectionLabel(toolId: string): string {
  return TOOL_NAMES[toolId] ?? toolId.toUpperCase();
}

function normalizeSections(raw: unknown): SyqiSections {
  if (!raw || typeof raw !== 'object') return {};
  const out: SyqiSections = {};
  for (const gear of GEARS) {
    const list = (raw as Record<string, unknown>)[gear];
    if (Array.isArray(list)) out[gear] = list.filter((t): t is string => typeof t === 'string');
  }
  return out;
}

/**
 * Read/write the `sections` key of user_settings.syqi_widgets, preserving
 * every other key in the column (the legacy `widgets` layout).
 */
export function useSyqiSections(userId: string | undefined): {
  sections: SyqiSections;
  setToolEnabled: (gear: Gear, toolId: string, enabled: boolean) => void;
  loading: boolean;
} {
  const { value, setValue, loading } = useSettingColumn<Record<string, unknown> | null>(userId, 'syqi_widgets', null);
  const sections = normalizeSections(value?.sections);

  const setToolEnabled = (gear: Gear, toolId: string, enabled: boolean) => {
    const current = enabledToolsFor(sections, gear);
    const next = enabled
      ? SECTION_TOOLS[gear].filter((id) => current.includes(id) || id === toolId)
      : current.filter((id) => id !== toolId);
    setValue({ ...(value ?? {}), sections: { ...sections, [gear]: next } });
  };

  return { sections, setToolEnabled, loading };
}

// ── SyQi Overview settings (Settings → SyQi → Dashboard, "Overview" band) ──
// Storage: user_settings.syqi_widgets jsonb gains an OPTIONAL `overview` key
// ({ prompt: string }) alongside `sections` and the legacy `widgets` array.
// Read by the SyqiOverview panel on the home screen.
//
// The old `mode` key ('text'|'video'|'voice') is dead as of 2026-07-26 — one
// AI module renders everywhere and video on/off is a runtime toggle in its
// ⋮ menu, persisted per screen in AsyncStorage. Stale `mode` values still in
// the column are simply ignored.

export type SyqiOverviewSettings = {
  prompt: string;
};

const DEFAULT_OVERVIEW: SyqiOverviewSettings = { prompt: '' };

function normalizeOverview(raw: unknown): SyqiOverviewSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_OVERVIEW };
  const obj = raw as Record<string, unknown>;
  const prompt = typeof obj.prompt === 'string' ? obj.prompt : DEFAULT_OVERVIEW.prompt;
  return { prompt };
}

/**
 * Read/write the `overview` key of user_settings.syqi_widgets, preserving
 * every other key in the column (the `sections` key and the legacy
 * `widgets` layout).
 */
export function useSyqiOverviewSettings(userId: string | undefined): {
  overview: SyqiOverviewSettings;
  setOverview: (next: Partial<SyqiOverviewSettings>) => void;
  loading: boolean;
} {
  const { value, setValue, loading } = useSettingColumn<Record<string, unknown> | null>(userId, 'syqi_widgets', null);
  const overview = normalizeOverview(value?.overview);

  const setOverview = (next: Partial<SyqiOverviewSettings>) => {
    setValue({ ...(value ?? {}), overview: { ...overview, ...next } });
  };

  return { overview, setOverview, loading };
}

/**
 * Combined sections + overview read/write over a SINGLE underlying
 * `syqi_widgets` column instance. Use this (instead of co-mounting
 * useSyqiSections + useSyqiOverviewSettings) whenever both pieces of state
 * are edited in the same screen — two independent useSettingColumn mounts
 * each cache their own stale copy of the column, so a write from one hook
 * clobbers a write from the other (e.g. SyqiDashboardEditor toggling a tool
 * then editing the overview prompt in the same session).
 */
export function useSyqiWidgets(userId: string | undefined): {
  sections: SyqiSections;
  setToolEnabled: (gear: Gear, toolId: string, enabled: boolean) => void;
  overview: SyqiOverviewSettings;
  setOverview: (next: Partial<SyqiOverviewSettings>) => void;
  loading: boolean;
} {
  const { value, setValue, loading } = useSettingColumn<Record<string, unknown> | null>(userId, 'syqi_widgets', null);
  const sections = normalizeSections(value?.sections);
  const overview = normalizeOverview(value?.overview);

  const setToolEnabled = (gear: Gear, toolId: string, enabled: boolean) => {
    const current = enabledToolsFor(sections, gear);
    const next = enabled
      ? SECTION_TOOLS[gear].filter((id) => current.includes(id) || id === toolId)
      : current.filter((id) => id !== toolId);
    setValue({ ...(value ?? {}), sections: { ...sections, [gear]: next } });
  };

  const setOverview = (next: Partial<SyqiOverviewSettings>) => {
    setValue({ ...(value ?? {}), overview: { ...overview, ...next } });
  };

  return { sections, setToolEnabled, overview, setOverview, loading };
}
